import { useQuery } from "@tanstack/react-query";
import axios from "../utils/axios-instance";
import { ServerRoutes } from "../routes";
import { watchProgress } from "../types/watchProgress";

import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Typography,
} from "@mui/material";
import BaseBarChart from "./Base/BaseBarChart";

function CourseChart({ courseId }: { courseId: number }) {
  const fetchCourseWatchProgress = async () => {
    const response = await axios.get<watchProgress>(
      `${ServerRoutes.courseWatchProgress}?courseId=${courseId}`,
    );
    return response.data;
  };

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["courseWatchProgress", courseId],
    queryFn: fetchCourseWatchProgress,
    enabled: !!courseId,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          height: 400,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Failed to load course statistics.
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No statistics available.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      {isFetching && !isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "rgba(255,255,255,0.8)",
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
          }}
        >
          <CircularProgress size={16} />
          <Typography variant="caption">Updating...</Typography>
        </Box>
      )}

      <BaseBarChart data={data} />
    </Box>
  );
}

export default CourseChart;
